import api from './utils/api';

// Central registry of active "what's new" feature spotlights.
//
// Every new visible UI thing that uses <FeatureSpotlight> SHOULD be listed
// here so it shows up in the "What's new" pill (header) and Changelog page.
// Items disappear from the "unread" list as soon as the user dismisses the
// inline spotlight (which writes `feature_seen_v1__<key>__<userId>` to
// localStorage). Items the user can't see (wrong role/module) are filtered out.
//
// Fields:
//   key:        same string passed to useFeatureSpotlight(key)
//   path:       where "Try it" should navigate
//   titleKey:   i18n key for the short title (reuse the inline spotlight's key)
//   bodyKey:    i18n key for the body
//   moduleKey:  nav key to translate via t('nav.<moduleKey>') for the pill
//   addedAt:    YYYY-MM-DD — used to sort newest first and to age-out (90d)
//   isVisibleTo:(user) => boolean — defaults to "any internal user"
//
// When you add a new spotlight to a page, ALSO append an entry here.

const ANY_INTERNAL = (u) => !!u && u.role !== 'client';
const ADMIN_ONLY = (u) => u?.role === 'admin';

export const FEATURE_SPOTLIGHTS = [
  {
    key: 'monitoring_install_short_link_v1',
    path: '/monitoring/install',
    titleKey: 'monitoring.install.spotShortTitle',
    bodyKey: 'monitoring.install.spotShortBody',
    moduleKey: 'monitoring',
    addedAt: '2026-08-21',
    isVisibleTo: ANY_INTERNAL,
  },
  {
    key: 'companies_isp_links_card',
    path: '/companies',
    titleKey: 'companies.ispLinksSpotlightTitle',
    bodyKey: 'companies.ispLinksSpotlightBody',
    moduleKey: 'companies',
    addedAt: '2026-06-12',
    isVisibleTo: ANY_INTERNAL,
  },
  {
    key: 'web_analytics_v1',
    path: '/dashboard',
    titleKey: 'webAnalytics.spotlightTitle',
    bodyKey: 'webAnalytics.spotlightBody',
    moduleKey: 'dashboard',
    addedAt: '2026-05-14',
    isVisibleTo: ADMIN_ONLY,
  },
  {
    key: 'reports_email_opens_tracker',
    path: '/reports',
    titleKey: 'reports.spotlightTitle',
    bodyKey: 'reports.spotlightBody',
    moduleKey: 'reports',
    addedAt: '2026-05-12',
    isVisibleTo: ANY_INTERNAL,
  },
  {
    key: 'contacts_merge_from_profile',
    path: '/companies',
    titleKey: 'contacts.mergeSpotlightTitle',
    bodyKey: 'contacts.mergeSpotlightBody',
    moduleKey: 'companies',
    addedAt: '2026-05-26',
    isVisibleTo: ANY_INTERNAL,
  },
];

const STORAGE_PREFIX = 'feature_seen_v1__';
const MAX_AGE_DAYS = 90;

function seenKey(key, userId) {
  return userId ? `${STORAGE_PREFIX}${key}__${userId}` : `${STORAGE_PREFIX}${key}`;
}

// ── Server-synced seen state ──────────────────────────────────────────────
// The spotlight_seen table is the source of truth so a dismissal on one device
// is reflected on every device and survives a cache clear. localStorage is kept
// as a fast offline cache that mirrors the server. This in-memory Set is the
// live view both the inline hook (useFeatureSpotlight) and the changelog
// "Try these" card (WhatsNewSection) read from; it's seeded from localStorage
// instantly, then reconciled with the server once `ensureSeenLoaded` resolves.
let _userId = null;
let _seen = new Set();
let _loaded = false;
let _inflight = null;
const _subs = new Set();

function _emit() {
  _subs.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

// Subscribe to seen-state changes (server load, dismiss, user switch). Returns
// an unsubscribe fn. Consumers bump a render tick so their seen-checks re-run.
export function subscribeSeen(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}

function _seedFromLocal(userId) {
  try {
    const suffix = `__${userId}`;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX) && k.endsWith(suffix)) {
        _seen.add(k.slice(STORAGE_PREFIX.length, k.length - suffix.length));
      }
    }
  } catch { /* ignore */ }
}

// Lazily load the caller's seen keys from the server, once per user. Safe to
// call from every spotlight on every render — it dedupes via _loaded/_inflight.
// On a user switch it resets and reloads. On network failure it degrades to the
// localStorage cache (still marks loaded so the UI isn't stuck hidden forever).
export function ensureSeenLoaded(userId) {
  if (!userId) return;
  if (userId !== _userId) {
    _userId = userId;
    _seen = new Set();
    _loaded = false;
    _inflight = null;
    _seedFromLocal(userId);
    _emit();
  }
  if (_loaded || _inflight) return;
  _inflight = api.get('/spotlights/seen')
    .then((res) => {
      const keys = Array.isArray(res.data?.keys) ? res.data.keys : [];
      keys.forEach((k) => {
        _seen.add(k);
        try { localStorage.setItem(seenKey(k, userId), String(Date.now())); } catch { /* ignore */ }
      });
    })
    .catch(() => { /* offline / route absent — fall back to localStorage cache */ })
    .finally(() => { _loaded = true; _inflight = null; _emit(); });
}

export function isSeenLoaded() {
  return _loaded;
}

export function isSeen(key, userId) {
  if (_seen.has(key)) return true;
  // Fallback for reads before the store has loaded (or for a different user id).
  try {
    return !!localStorage.getItem(seenKey(key, userId));
  } catch {
    return false;
  }
}

export function markSeen(key, userId) {
  _seen.add(key);
  try {
    localStorage.setItem(seenKey(key, userId), String(Date.now()));
  } catch {
    // ignore
  }
  _emit();
  // Persist to the server (source of truth). Idempotent server-side; if it
  // fails the localStorage cache still covers this device until next sync.
  try { api.post('/spotlights/seen', { key }).catch(() => { /* non-critical */ }); } catch { /* ignore */ }
}

export function getVisibleSpotlights(user) {
  if (!user) return [];
  const cutoff = Date.now() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  return FEATURE_SPOTLIGHTS
    .filter((s) => (s.isVisibleTo ? s.isVisibleTo(user) : ANY_INTERNAL(user)))
    .filter((s) => {
      if (!s.addedAt) return true;
      const ts = Date.parse(s.addedAt);
      return Number.isFinite(ts) ? ts >= cutoff : true;
    })
    .sort((a, b) => (a.addedAt < b.addedAt ? 1 : -1));
}

export function getUnseenSpotlights(user) {
  return getVisibleSpotlights(user).filter((s) => !isSeen(s.key, user?.id));
}

// Global on/off switch — when a user pauses spotlights, the pill hides, inline
// callouts stop firing, and the changelog "Try these" section collapses to a
// muted "paused" row with a reactivate button. Per-user localStorage flag.
const DISABLED_PREFIX = 'spotlight_disabled_v1__';

function disabledKey(userId) {
  return userId ? `${DISABLED_PREFIX}${userId}` : `${DISABLED_PREFIX}anon`;
}

export function isSpotlightsDisabled(userId) {
  try {
    return localStorage.getItem(disabledKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function setSpotlightsDisabled(userId, disabled) {
  try {
    if (disabled) localStorage.setItem(disabledKey(userId), '1');
    else localStorage.removeItem(disabledKey(userId));
  } catch {
    // ignore
  }
}

// Force-show mechanism: when the user clicks "Try it" from the popover, we
// stash the key in sessionStorage. The destination page's useFeatureSpotlight
// hook reads + clears it, then re-fires the spotlight even if previously seen.
const FORCE_KEY = 'spotlight_force_v1';

export function requestForceShow(key) {
  try {
    sessionStorage.setItem(FORCE_KEY, key);
  } catch {
    // ignore
  }
}

export function consumeForceShow(key) {
  try {
    const pending = sessionStorage.getItem(FORCE_KEY);
    if (pending === key) {
      sessionStorage.removeItem(FORCE_KEY);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}
